import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import { featureRequestApi } from '../api/endpoints';
import { getApiErrorMessage } from '../api/client';
import { formatDate } from '../utils/date';
import { usePermission } from '../auth/usePermission';
import { useProjects } from '../context/ProjectContext';
import { FEATURE_REQUEST_STATUS_META, FEATURE_REQUEST_STATUS_OPTIONS } from './FeatureRequests/statusMeta';
import { RequestFormDialog } from './FeatureRequests/RequestFormDialog';
import { RequestDetailDialog } from './FeatureRequests/RequestDetailDialog';
import type { FeatureRequest, FeatureRequestStatus } from '../types';

type StatusFilter = 'ALL' | FeatureRequestStatus;

/** Màn hình danh sách yêu cầu chức năng (route /feature-requests). */
export function FeatureRequests(): JSX.Element {
  const canCreate = usePermission('FEATURE_REQUEST_CREATE');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [items, setItems] = useState<FeatureRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState<FeatureRequest | null>(null);
  const [searchParams] = useSearchParams();
  const { projects } = useProjects();
  const rawProjectId = searchParams.get('projectId');
  // Chỉ prefill field "Dự án" khi mở form thủ công — màn Chi tiết dự án tự
  // mở popup tạo yêu cầu tại chỗ (không điều hướng qua trang này nữa), nên
  // route này không còn cần tự mở theo query param.
  const validInitialProjectId = rawProjectId && projects.some((p) => p.id === rawProjectId)
    ? rawProjectId
    : undefined;

  useEffect(() => {
    setPage(0);
  }, [statusFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await featureRequestApi.list({
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Không thể tải danh sách yêu cầu.'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page, rowsPerPage]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          Yêu cầu chức năng
        </Typography>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setFormOpen(true)}>
            Tạo yêu cầu
          </Button>
        )}
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2.5 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="fr-status-filter-label">Trạng thái</InputLabel>
              <Select
                labelId="fr-status-filter-label"
                label="Trạng thái"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              >
                <MenuItem value="ALL">Tất cả trạng thái</MenuItem>
                {FEATURE_REQUEST_STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {FEATURE_REQUEST_STATUS_META[s].label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tên yêu cầu</TableCell>
                <TableCell>Dự án / Giai đoạn</TableCell>
                <TableCell>Trạng thái</TableCell>
                <TableCell>Người tạo</TableCell>
                <TableCell>Ngày tạo</TableCell>
                <TableCell>File</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Alert severity="error">{error}</Alert>
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Chưa có yêu cầu nào.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((fr) => {
                  const meta = FEATURE_REQUEST_STATUS_META[fr.status];
                  return (
                    <TableRow key={fr.id} hover onClick={() => setSelected(fr)} sx={{ cursor: 'pointer' }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {fr.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {fr.project_name ? `${fr.project_name}${fr.phase_name ? ` › ${fr.phase_name}` : ''}` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={meta.label}
                          color={meta.color === 'default' ? 'default' : meta.color}
                          size="small"
                          variant={meta.color === 'default' ? 'outlined' : 'filled'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {fr.created_by_name ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(fr.created_at)}</TableCell>
                      <TableCell>{fr.file_count}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Số dòng/trang"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}`}
        />
      </Paper>

      <RequestFormDialog
        open={formOpen}
        request={null}
        initialProjectId={validInitialProjectId}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          setFormOpen(false);
          void load();
        }}
      />

      {selected && (
        <RequestDetailDialog
          request={selected}
          onClose={() => setSelected(null)}
          onChanged={() => void load()}
        />
      )}
    </Box>
  );
}
